import React, { useEffect, useRef, useState } from "react";

const defaultCenter = { lat: 37.4239163, lng: -122.0947209 };

const isGoogleMapsReady = () =>
  typeof window !== "undefined" &&
  !!window.google?.maps &&
  typeof window.google.maps.importLibrary === "function";

// Placeholder shown wherever a map cannot be drawn. Deliberately quiet —
// a map is supporting detail on these screens, so a failure should read as
// "no map" rather than as the page being broken.
const MapPlaceholder = ({ style, label }) => (
  <div
    style={{
      ...style,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#F2F4F7",
      border: "1px solid #E5E7EB",
      borderRadius: 8,
      color: "#949DAA",
      fontSize: 12,
      fontFamily: "Inter Tight",
      textAlign: "center",
      padding: 8,
      boxSizing: "border-box",
    }}
  >
    {label}
  </div>
);

const MapsView = ({ details, mapContainerStyle, interactive = true }) => {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [location, setLocation] = useState(defaultCenter);
  const [isLoaded, setIsLoaded] = useState(isGoogleMapsReady());
  const [loadError, setLoadError] = useState(false);
  // Set by the gm_authFailure hook in index.html when Google refuses the key.
  const [authFailed, setAuthFailed] = useState(
    typeof window !== "undefined" && !!window.__googleMapsAuthFailed,
  );

  useEffect(() => {
    const onAuthFail = () => setAuthFailed(true);
    window.addEventListener("googleMapsAuthFailed", onAuthFail);
    return () => window.removeEventListener("googleMapsAuthFailed", onAuthFail);
  }, []);

  useEffect(() => {
    if (isLoaded) return;

    let cancelled = false;
    let retryCount = 0;
    const maxRetries = 150; // ~15s
    let timerId;

    const checkMapsLoaded = () => {
      if (cancelled) return;
      if (isGoogleMapsReady()) {
        setIsLoaded(true);
        setLoadError(false);
      } else if (retryCount < maxRetries) {
        retryCount++;
        timerId = setTimeout(checkMapsLoaded, 100);
      } else {
        setLoadError(true);
        console.error("Google Maps API failed to load");
      }
    };

    checkMapsLoaded();

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;

    if (details?.lat != null && details?.lng != null) {
      setLocation({ lat: details.lat, lng: details.lng });
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => console.error("Error getting location:", error),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  }, [isLoaded, details]);

  useEffect(() => {
    if (!isLoaded) return;

    let cancelled = false;

    async function initMap() {
      try {
        const { Map } = await window.google.maps.importLibrary("maps");
        const { AdvancedMarkerElement } = await window.google.maps.importLibrary(
          "marker"
        );

        if (cancelled || !mapRef.current) return;

        const map = new Map(mapRef.current, {
          center: location,
          zoom: 14,
          mapId: "4504f8b37365c3d0",
          disableDefaultUI: true,
          zoomControl: interactive,
          fullscreenControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          rotateControl: false,
          scaleControl: false,
          keyboardShortcuts: false,
          clickableIcons: false,
          gestureHandling: interactive ? "auto" : "none",
        });

        if (markerRef.current) {
          markerRef.current.map = null;
        }

        markerRef.current = new AdvancedMarkerElement({
          map,
          position: location,
          title: "Selected Location",
        });
      } catch (error) {
        console.error("Error initializing map:", error);
      }
    }

    initMap();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, location, interactive]);

  // Key refused — never mount the map, or Google paints its own grey
  // "Sorry! Something went wrong" panel over the container.
  if (authFailed) {
    return <MapPlaceholder style={mapContainerStyle} label="Map unavailable" />;
  }
  if (loadError) {
    return <MapPlaceholder style={mapContainerStyle} label="Map unavailable" />;
  }
  if (!isLoaded) {
    return <MapPlaceholder style={mapContainerStyle} label="Loading map…" />;
  }

  return <div ref={mapRef} style={mapContainerStyle} />;
};

export default MapsView;
